import React, { useState } from "react";
import {
    Box,
    Divider,
    FormHelperText,
    Grid,
    SwipeableDrawer,
    Typography,
    makeStyles,
} from "@material-ui/core";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { useDispatch, useSelector } from "react-redux";

import { BUTTON_COLOR } from "../../utils/constant";
import { applied_offer, clear_applied_offer } from "../../store/actions/action";
import { addDiscount, removeDiscount } from "../../store/actions/cartActions";
import { routeOfferThroughEngine } from "../../utils/offer_router";

const useStyles = makeStyles({
    drawerPaper: {
        borderTopLeftRadius: "20px",
        borderTopRightRadius: "20px",
    },
});

const isOfferActiveByScheduler = (offer, storeInfo, storeInfoConfig) => {
    if (!offer) return false;

    const now = new Date();
    const effectiveDate = offer?.offer_header?.effective_date;
    const endDate = offer?.offer_header?.end_date;
    if (effectiveDate && new Date(effectiveDate) > now) return false;
    if (endDate && new Date(endDate) < now) return false;

    const scheduler = offer?.offer_scheduler;
    if (!scheduler || !Array.isArray(scheduler.days) || scheduler.days.length === 0) return true;
    if (scheduler.is_all_days === true) return true;

    const timeZone =
        storeInfo?.store_timezone ||
        storeInfoConfig?.store_timezone ||
        storeInfoConfig?.general_config?.store_timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;

    const currentDay = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(now);

    const timeParts = new Intl.DateTimeFormat("en-US", {
        hour: "2-digit", minute: "2-digit", hour12: false, hourCycle: "h23", timeZone,
    }).formatToParts(now);
    let hour = timeParts.find((p) => p.type === "hour")?.value || "00";
    if (hour === "24") hour = "00";
    const minute = (timeParts.find((p) => p.type === "minute")?.value || "00").padStart(2, "0");
    const currentTime = `${hour.padStart(2, "0")}:${minute}`;

    const matchingDay = scheduler.days.find(
        (d) => (d?.day || "").toLowerCase() === currentDay.toLowerCase()
    );
    if (!matchingDay) return false;

    const timeFrames = matchingDay.time_frame;
    if (!timeFrames || !Array.isArray(timeFrames) || timeFrames.length === 0) return true;

    return timeFrames.some((tf) => {
        const start = tf?.start_time || "00:00";
        const end   = tf?.end_time   || "23:59";
        return currentTime >= start && currentTime <= end;
    });
};

// Pay-bill coupon drawer for FSR flow. Runs against activeOrder.item_details
// (already engine-shaped, itemsAreFlat: true). Uses the engine as the single
// source of truth for eligibility — no legacy cart-shape heuristics.
//
// Discard behavior: parent (Checkout.js) is responsible for clearing
// engineDiscount + applied_offer if user leaves without paying.
function FSRPayBillOffers({ open, setOpen, activeOrder }) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const cartProps = useSelector((s) => s.cartReducer) || {};
    const sessionReducer = useSelector((s) => s.sessionReducer) || {};

    const rawStoreOffer = cartProps?.storeOffer || [];
    const appliedOffer = cartProps?.appliedOffer || [];
    const engineDiscount = cartProps?.engineDiscount || null;
    const storeInfo = sessionReducer?.store_details || {};
    const storeInfoConfig = sessionReducer?.store_config;
    const storeId = sessionReducer?.store_details?.store_id;

    // Filter by scheduler up-front so any downstream use (coupon match, visible
    // list, applyOffer) only sees currently-active offers.
    const storeOffer = rawStoreOffer.filter((offer) => isOfferActiveByScheduler(offer, storeInfo, storeInfoConfig));
    const orderType = cartProps?.orderType?.order?.order_type || "Dine In";
    const primaryColor = storeInfoConfig?.general_config?.primary_color || BUTTON_COLOR;

    const items = activeOrder?.item_details || [];

    const [coupenCode, setCoupenCode] = useState("");
    const [invalidCoupen, setInvalidCoupen] = useState(false);
    const [applying, setApplying] = useState(false);

    const engineCtx = {
        orderType,
        storeId,
        paymentOption: cartProps?.selectedTender,
        storeConfig:   storeInfoConfig,
        itemsAreFlat:  true,
        tip:           cartProps?.tip || 0,
    };

    const applyOffer = async (offer, entryPoint = "list") => {
        setApplying(true);
        try {
            const termStyle = offer?.offer_header?.term_style;
            const term = offer?.offer_term?.[0];
            const noPerTrans = String(term?.no_pertrans ?? "1");

            let offerToApply = offer;

            // Mirror QSR CartOffers.js: when no specific items are bound
            // (is_include_items === false) and the discount is per-item
            // (no_pertrans === "0"), rewrite the offer to bind to the
            // cheapest item so the engine has something concrete to stamp.
            if (termStyle !== "C3" && noPerTrans === "0" && term?.is_include_items === false) {
                const activeItems = (items || []).filter(i => !i.is_void && !i.isVoid && !i.is_refund);
                if (activeItems.length) {
                    const cheapest = activeItems.reduce((min, it) => {
                        const price = parseFloat(it?.item_variant?.[0]?.plu ?? it?.extended_amount ?? 0);
                        const minPrice = parseFloat(min?.item_variant?.[0]?.plu ?? min?.extended_amount ?? 0);
                        return price < minPrice ? it : min;
                    }, activeItems[0]);

                    offerToApply = {
                        ...offer,
                        offer_term: offer.offer_term.map((t) => ({
                            ...t,
                            is_include_items: true,
                        })),
                        offer_term_product: [{
                            item_id: cheapest?.item_variant?.[0]?.sku ?? cheapest?.sku,
                            offer_term_id: term?.offer_term_id,
                            category_id: cheapest?.category_id,
                        }],
                    };
                }
            }

            // Don't pre-clear — let routeOfferThroughEngine's dispatch
            // atomically replace the current offer with the new one.
            dispatch(applied_offer(offerToApply));
            dispatch(addDiscount(offerToApply.offer_term, termStyle));
            const result = await routeOfferThroughEngine(offerToApply, items, { ...engineCtx, entryPoint }, dispatch);
            // Engine is the sole authority — zero discount means the offer
            // doesn't apply to this cart (minimum-spend, no qualifying items, etc.).
            if (!result || !(result.total_discount > 0)) {
                dispatch(removeDiscount());
                dispatch(clear_applied_offer());
                dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
                setInvalidCoupen(true);
                return;
            }
            setInvalidCoupen(false);
            setCoupenCode("");
            setOpen(false);
        } finally {
            setApplying(false);
        }
    };

    const removeOffer = () => {
        dispatch(removeDiscount());
        dispatch(clear_applied_offer());
        dispatch({ type: "CLEAR_ENGINE_DISCOUNT" });
        setOpen(false);
    };

    const handleCouponSubmit = async () => {
        const code = (coupenCode || "").trim().toLowerCase();
        if (!code) return;
        const matched = (storeOffer || []).find((o) =>
            (o?.offer_term || []).some(
                (t) => (t?.coupon_code || "").toLowerCase() === code
            )
            || (o?.promo_code || "").toLowerCase() === code
            || (o?.code || "").toLowerCase() === code
            || (o?.title || "").toLowerCase() === code
            || (o?.offer_name || "").toLowerCase() === code
        );
        if (!matched) {
            setInvalidCoupen(true);
            return;
        }
        await applyOffer(matched, "coupon");
    };

    const currentlyApplied = appliedOffer && appliedOffer.length > 0;
    const appliedOfferId = engineDiscount?.offer_id;

    // Default list hides any offer that has a coupon/promo code,
    // UNLESS that discount is currently applied.
    const visibleOffers = (storeOffer || []).filter((offer) => {
        const isApplied = currentlyApplied && appliedOfferId === offer?.offer_id;
        if (isApplied) return true;

        const promoCodes = [
            offer?.promo_code,
            offer?.code,
            ...(offer?.offer_term || []).map((t) => t?.coupon_code),
        ]
            .filter(Boolean)
            .map((c) => String(c).trim().toLowerCase());
        return promoCodes.length === 0;
    });

    return (
        <div>
            <SwipeableDrawer
                open={open}
                anchor={"bottom"}
                onClose={() => setOpen(false)}
                onOpen={() => setOpen(true)}
                disableSwipeToOpen={true}
                classes={{
                    paper: classes.drawerPaper,
                }}
            >
                {(storeOffer || []).length ? (
                    <Typography style={{ padding: "2rem 2rem 0rem 1rem" }}>
                        <span style={{ fontSize: "1.3rem", fontWeight: "bold", color: primaryColor }}>
                            Enter Promo Code
                        </span>
                        <br />
                    </Typography>
                ) : (
                    <Typography style={{ padding: "2rem" }}>
                        You don't have any offers right now.
                    </Typography>
                )}
                <ClearIcon
                    style={{
                        marginRight: "0.5rem",
                        position: "absolute",
                        top: "2rem",
                        right: "15px",
                        color: primaryColor,
                    }}
                    onClick={() => setOpen(false)}
                />

                {(storeOffer || []).length ? (
                    <Box sx={{ padding: "1rem" }} role="presentation">
                        <div style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
                            <div style={{
                                flex: 1,
                                border: `1.5px solid ${primaryColor || BUTTON_COLOR}`,
                                borderRadius: "6px",
                                display: "flex",
                                alignItems: "center",
                                padding: "0 10px",
                                background: "#fff",
                            }}>
                                <input
                                    type="text"
                                    placeholder="Enter Coupon Code"
                                    value={coupenCode}
                                    onChange={(e) => { setCoupenCode(e.target.value.toUpperCase()); setInvalidCoupen(false); }}
                                    style={{
                                        border: "none",
                                        outline: "none",
                                        width: "100%",
                                        padding: "12px 0",
                                        fontSize: "1rem",
                                        background: "transparent",
                                        color: "#333",
                                    }}
                                />
                                {coupenCode && (
                                    <ClearIcon
                                        style={{ color: "#999", cursor: "pointer", fontSize: "1.2rem" }}
                                        onClick={() => { setCoupenCode(""); setInvalidCoupen(false); }}
                                    />
                                )}
                            </div>
                            <button
                                onClick={handleCouponSubmit}
                                disabled={applying || !coupenCode.trim()}
                                style={{
                                    background: primaryColor || BUTTON_COLOR,
                                    color: "#fff",
                                    border: "none",
                                    padding: "0 24px",
                                    borderRadius: "6px",
                                    fontSize: "1rem",
                                    fontWeight: "bold",
                                    cursor: applying ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                Apply
                            </button>
                        </div>
                        <FormHelperText style={{ marginLeft: "0.4rem" }}>
                            {invalidCoupen && "Invalid Coupon Code"}
                        </FormHelperText>
                        <Divider style={{ marginTop: "1.5rem" }} />

                        <Grid style={{ maxHeight: "40vh", overflow: "scroll" }}>
                            {visibleOffers.map((offer) => {
                                const isApplied = currentlyApplied && appliedOfferId === offer?.offer_id;
                                return (
                                    <Grid
                                        key={offer?.offer_id}
                                        style={{
                                            display: "flex",
                                            margin: "10px 0",
                                            border: "1px solid #e0e0e0",
                                            borderLeft: "none",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            background: "#fff",
                                        }}
                                    >
                                        {/* Left Ticket Stub */}
                                        <div style={{
                                            width: "24px",
                                            backgroundColor: primaryColor || BUTTON_COLOR,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-evenly",
                                            alignItems: "flex-start",
                                        }}>
                                            <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }} />
                                            <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }} />
                                            <div style={{ width: "8px", height: "12px", backgroundColor: "#fff", borderRadius: "0 10px 10px 0" }} />
                                        </div>

                                        {/* Right Content */}
                                        <div style={{
                                            flex: 1,
                                            padding: "12px",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "4px",
                                        }}>
                                            {/* Top Row: Title & Button */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <span style={{ fontSize: "1rem", fontWeight: "600", color: "#333", paddingRight: "10px" }}>
                                                    {offer?.offer_header?.deal_type}
                                                </span>
                                                <button
                                                    disabled={applying}
                                                    onClick={() => (isApplied ? removeOffer() : applyOffer(offer))}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        color: primaryColor || BUTTON_COLOR,
                                                        fontWeight: "bold",
                                                        fontSize: "0.95rem",
                                                        cursor: applying ? "not-allowed" : "pointer",
                                                        textTransform: "uppercase",
                                                        padding: "0",
                                                        lineHeight: "1.2rem",
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {isApplied ? "REMOVE" : "APPLY"}
                                                </button>
                                            </div>
                                            {/* Bottom Row: Description */}
                                            <span style={{ fontSize: "0.85rem", color: "#888", paddingRight: "40px" }}>
                                                {offer?.offer_header?.terms_conditions}
                                            </span>
                                        </div>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                ) : (
                    ""
                )}
            </SwipeableDrawer>
        </div>
    );
}

export default FSRPayBillOffers;
